import {
  EyeOutlined,
  MessageOutlined,
  LoadingOutlined,
  DownloadOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useAppSelector } from 'app/hooks';
import EmployeeColumn from 'components/EmployeeColumn';
import ITVTable from 'components/ITVTable';
import MenuAction from 'components/MenuAction';
import PageHeader from 'components/PageHeader';
import { selectConfiguration } from 'features/configuration/configurationSlice';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getManagerView, exportEmployeePerformanceHistory } from '../performanceAction';
import { getUser } from 'utils/auth';
import { selectPerformanceData } from '../performanceSlice';
import ITVSearch from 'components/ITVSearch';
import { useTranslation } from 'react-i18next';
import Note from 'components/Note';
import { checkPermission } from 'utils/function';
import { Checkbox, Button, Modal, Form, Select, DatePicker } from 'antd';
import moment from 'moment';
import axiosClient from 'api/axiosClient';
import { notificationToast } from 'components/notificationToast';
import { Notification } from 'constants/notification';

export default function ManagerViewPage() {
  const { t } = useTranslation(['performance']);
  const dispatch = useDispatch();
  const location: any = useLocation();
  const history = useHistory();
  const menuStore = useAppSelector(selectConfiguration);
  const performanceStore = useAppSelector(selectPerformanceData);
  const managerViewData = performanceStore?.managerViewData;
  const dataScreenManage = useMemo(() => {
    return ((menuStore?.dataScreenManage as any)?.result || []) as any[];
  }, [menuStore?.dataScreenManage]);
  const [titleHeader, setTitleHeader] = useState('');
  const [sizePage, setSizePage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedReviewId, setSelectedReviewId] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportForm] = Form.useForm();
  const [cycleList, setCycleList] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: sizePage,
          searchText: search,
          managerId: showAll ? undefined : getUser()?.employeeId,
        };
        await dispatch(getManagerView(params));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, sizePage, currentPage, search, showAll]);

  useEffect(() => {
    const temp = dataScreenManage
      ?.filter((item: any) => location.pathname === item.path)
      ?.map((item: any) => item.nameScreen);

    setTitleHeader(temp ? temp[0] : '');
  }, [dataScreenManage, location.pathname]);

  useEffect(() => {
    // Fetch cycle list for export filter
    const fetchCycles = async () => {
      try {
        const response = await axiosClient.get('/performance/review/cycles');
        if (response?.data) {
          setCycleList(response.data);
        }
      } catch (error) {
        console.error('Error fetching cycles:', error);
      }
    };
    fetchCycles();
  }, []);

  const handleExport = async () => {
    if (selectedEmployees.length === 0) {
      notificationToast(
        Notification.Type.Error,
        'Please select at least one employee to export',
        3,
      );
      return;
    }

    if (selectedEmployees.length > 50) {
      notificationToast(Notification.Type.Error, 'Cannot export more than 50 employees at once', 3);
      return;
    }

    setIsExportModalVisible(true);
  };

  const handleExportConfirm = async () => {
    try {
      const values = await exportForm.validateFields();
      const params: any = {
        employeeIds: selectedEmployees,
        includeNotes: values.includeNotes || false,
        includeSalaryHistory: values.includeSalaryHistory || false,
      };

      if (values.reviewCycleId) {
        params.reviewCycleId = values.reviewCycleId;
      }

      if (values.dateRange && values.dateRange.length === 2) {
        params.fromDate = values.dateRange[0].format('YYYY-MM-DD');
        params.toDate = values.dateRange[1].format('YYYY-MM-DD');
      }

      await dispatch(exportEmployeePerformanceHistory(params));
      setIsExportModalVisible(false);
      exportForm.resetFields();
      setSelectedEmployees([]);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const menu = [
    {
      name: t('performance:performance_review.view_detail'),
      icon: <EyeOutlined style={{ color: '#1890FF' }} />,
      handleClick: (value: any) => {
        const employeeId = value?.employeeId || value?.id;
        const reviewCycleId = value?.reviewCycleId || value?.revieCycleId;
        const assessmentId = value?.assessmentId;
        if (!employeeId) return;
        const pathname = reviewCycleId
          ? `/performance/employee-pr-management/${employeeId}/${reviewCycleId}`
          : `/performance/employee-pr-management/${employeeId}`;
        history.push({
          pathname,
          state: {
            ...value,
            employeeId,
            reviewCycleId,
            assessmentId,
          },
        });
      },
    },
    {
      name: 'Note',
      icon: <MessageOutlined style={{ color: '#1890FF' }} />,
      handleClick: (value: any) => {
        setSelectedUserId(value?.id);
        setSelectedReviewId(value?.reviewCycleId);
        setIsNoteVisible(true);
      },
    },
  ];

  const columns = [
    {
      title: '#',
      key: 'index',
      dataIndex: 'index',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (_: any, __: any, index: any) => {
        const trueIndex = index + sizePage * (currentPage - 1);
        return <div>{trueIndex + 1}</div>;
      },
    },
    {
      title: t('performance:pending_review.employee_name'),
      key: 'fullName',
      dataIndex: 'fullName',
      width: 100,
      minWidth: 100,
      render: (_: any, record: any) => {
        return (
          <EmployeeColumn
            avatar={
              record?.full_name
                ? `${record?.full_name}`
                : `${record?.lastName} ${record?.firstName}`
            }
            fullName={
              record?.full_name
                ? `${record?.full_name}`
                : `${record?.lastName} ${record?.firstName}`
            }
            email={record?.email}
          />
        );
      },
    },
    {
      title: t('performance:pending_review.employee_id'),
      key: 'employeeId',
      dataIndex: 'employeeId',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (_: any, record: any) => record?.employee_id,
    },
    {
      title: 'Line',
      key: 'line',
      dataIndex: 'line',
      width: 120,
      minWidth: 120,
      align: 'center',
      render: (_: any, record: any) => record?.line || record?.projectName || '',
    },
    // {
    //   title: 'Overal Score',
    //   key: 'total_rating',
    //   dataIndex: 'total_rating',
    //   width: 100,
    //   minWidth: 100,
    //   align: 'center',
    //   render: (text: any) => text || '',
    // },
    {
      title: t('performance:pending_review.job_title'),
      key: 'job_title',
      dataIndex: 'job_title',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: any) => text || '',
    },
    {
      title: t('performance:pending_review.direct_manager'),
      key: 'directManager',
      dataIndex: 'directManager',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: any) => text || '',
    },
    {
      title: t('performance:pending_review.current_level'),
      key: 'level',
      dataIndex: 'level',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: any) => text || '',
    },
    {
      title: t('performance:pending_review.action'),
      key: 'action',
      dataIndex: 'action',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: any, record: any) => (
        <div
          onClick={(e) => {
            e.stopPropagation(); // Ngăn click này lan ra hàng
          }}
        >
          <MenuAction menu={menu} data={record} />
        </div>
      ),
    },
  ];

  const handleSearch = (text: string) => {
    setSearch(text);
  };

  return (
    <>
      <PageHeader title={titleHeader} />
      <ITVSearch style={{ marginBottom: '1rem' }} handleSearch={handleSearch} />
      <div
        style={{
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={selectedEmployees.length === 0}
            onClick={handleExport}
          >
            {t('performance:export_selected', `Export Selected (${selectedEmployees.length})`)}
          </Button>
        </div>
        {checkPermission(getUser()?.permissions, 'SETUP_PAYROLL', getUser()?.isSystemAdmin) && (
          <Checkbox
            checked={showAll}
            onChange={(e) => {
              setShowAll(e.target.checked);
              setCurrentPage(1);
            }}
          >
            {t('performance:show_all_employees', 'Show all employees')}
          </Checkbox>
        )}
      </div>
      <ITVTable
        data={managerViewData?.result || []}
        columns={columns}
        isRowSelect={true}
        selectedRowKeys={selectedEmployees}
        onSelectedRowKeysChange={setSelectedEmployees}
        setOffset={setCurrentPage}
        offset={currentPage}
        setLimit={setSizePage}
        limit={sizePage}
        loading={loading}
        totalResult={managerViewData?.totalResult || undefined}
        onRow={(record: any) => ({
          onClick: () => {
            const employeeId = record?.employeeId || record?.id;
            const reviewCycleId = record?.reviewCycleId || record?.revieCycleId;
            const assessmentId = record?.assessmentId;
            if (!employeeId) return;
            const pathname = reviewCycleId
              ? `/performance/employee-pr-management/${employeeId}/${reviewCycleId}`
              : `/performance/employee-pr-management/${employeeId}`;
            history.push({
              pathname,
              state: {
                ...record,
                employeeId,
                reviewCycleId,
                assessmentId,
              },
            });
          },
        })}
      />
      {/* Note Modal */}
      <Note
        visible={isNoteVisible}
        onClose={() => setIsNoteVisible(false)}
        userId={selectedUserId}
        contextType={selectedReviewId ? 'REVIEW_ASSESSMENT' : 'PERFORMANCE_REVIEW'}
        idType={selectedReviewId}
      />

      {/* Export Modal */}
      <Modal
        title={t('performance:export_options', 'Export Options')}
        visible={isExportModalVisible}
        onOk={handleExportConfirm}
        onCancel={() => {
          setIsExportModalVisible(false);
          exportForm.resetFields();
        }}
        okText={t('common:export', 'Export')}
        cancelText={t('common:cancel', 'Cancel')}
        width={600}
      >
        <Form form={exportForm} layout="vertical">
          <Form.Item
            label={t('performance:filter_by_cycle', 'Filter by Review Cycle')}
            name="reviewCycleId"
          >
            <Select
              allowClear
              placeholder={t('performance:select_cycle', 'Select a review cycle (optional)')}
              options={cycleList.map((cycle: any) => ({
                label: cycle.name,
                value: cycle.id,
              }))}
            />
          </Form.Item>

          <Form.Item label={t('performance:date_range', 'Date Range')} name="dateRange">
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={[
                t('performance:from_date', 'From Date'),
                t('performance:to_date', 'To Date'),
              ]}
            />
          </Form.Item>

          <Form.Item name="includeNotes" valuePropName="checked" initialValue={true}>
            <Checkbox>{t('performance:include_notes', 'Include all notes and comments')}</Checkbox>
          </Form.Item>

          <Form.Item name="includeSalaryHistory" valuePropName="checked" initialValue={true}>
            <Checkbox>{t('performance:include_salary', 'Include salary history')}</Checkbox>
          </Form.Item>

          <div style={{ padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              <strong>{t('performance:export_info', 'Export Information:')}</strong>
            </p>
            <ul style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
              <li>
                {t('performance:export_info_1', 'Selected employees:')} {selectedEmployees.length}
              </li>
              <li>{t('performance:export_info_2', 'Each employee will have a summary sheet')}</li>
              <li>
                {t(
                  'performance:export_info_3',
                  'Detail sheets include competencies, goals, and ratings',
                )}
              </li>
              <li>{t('performance:export_info_4', 'File format: Excel (.xlsx)')}</li>
            </ul>
          </div>
        </Form>
      </Modal>
    </>
  );
}
